import { getBearerToken } from "../../../../api/postCall/bearerToken";
import {
  searchForMedicalPaOrdersFromPostgres,
  SearchForMedicalPaOrdersFromPostgresParams,
} from "../../../../api/postgreSQL/postCalls/searchForMedicalPaOrdersFromPostgres";
import { AuthStatusOptionModel } from "../../../../data-model/authStatusOptions";
import { PostgresResponseForMedicalPaOrders } from "../../../../data-model/postgresResponseForMedicalPaOrders";
import { GenericAuthStatusTableData } from "../../../../data-model/tablesData";
import { GenericAuthStatusState } from "../hooks/useGenericAuthStatusTableData";
import { generateGenericAuthStatusTableDataFromAlgoliaRecords } from "../utils/generateTableDataFromAlgoliaRecords";

/**
 * Executes a POST call to search for medical PA orders from Postgres and updates
 * both the API state and the rendered table data. Handles loading state and errors.
 *
 * @param payload - Search parameters for the Postgres query
 * @param authStatusOptions - Auth status options for formatting table badge data
 * @param setGenericAuthStatusState - State setter for API response metadata (loading, error, data)
 * @param setTableData - State setter for the processed table rows
 * @param setPaginationResetCount - Incrementing setter to trigger pagination reset
 */
export const makePostCallForGenericAuthStatus = async (
  payload: SearchForMedicalPaOrdersFromPostgresParams,
  authStatusOptions: AuthStatusOptionModel[],
  setGenericAuthStatusState: React.Dispatch<
    React.SetStateAction<GenericAuthStatusState>
  >,
  setTableData: React.Dispatch<
    React.SetStateAction<GenericAuthStatusTableData[]>
  >,
  setPaginationResetCount: React.Dispatch<React.SetStateAction<number>>,
) => {
  setGenericAuthStatusState((prev) => ({ ...prev, loading: true }));
  try {
    await getBearerToken();
    const response = await searchForMedicalPaOrdersFromPostgres(payload);
    const data = JSON.parse(response) as PostgresResponseForMedicalPaOrders;
    setGenericAuthStatusState((prev) => ({
      ...prev,
      data: data,
    }));
    setTableData(
      generateGenericAuthStatusTableDataFromAlgoliaRecords(
        data.hits ?? [],
        authStatusOptions,
      ),
    );
  } catch (error) {
    setGenericAuthStatusState((prev) => ({ ...prev, error: error as string }));
  } finally {
    setGenericAuthStatusState((prev) => ({ ...prev, loading: false }));
    setPaginationResetCount((prev) => prev + 1);
  }
};
